import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Observable, of } from 'rxjs';

import {
	DeviceAuthenticationState,
	DeviceErrorType,
	DeviceModel,
	DeviceStatus
} from '@shure/shared/angular/data-access/system-api/models';
import { IdButtonComponent } from '@shure/shared/angular/ui/components/buttons/id-button';
import { DeviceImageComponent } from '@shure/shared/angular/ui/components/device-image';
import { DeviceStateBarComponent } from '@shure/shared/angular/ui/components/device-state-bar';
import { InlineDeviceNameInputComponent } from '@shure/shared/angular/ui/components/inline-device-name-input';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

export interface DeviceNameCellData {
	id: string;
	model: DeviceModel;
	status: DeviceStatus;
	isVirtual: boolean;
	isPasswordEnabled: boolean;
	isAuthenticated: boolean;
	channelCount?: number;
	authenticatedState?: DeviceAuthenticationState;
	name: {
		name: string;
		isDisabled: boolean;
		constraints?: { maxLength?: number; pattern?: string };
	};
	identify?: {
		isDisabled: boolean;
		isIdentifying: boolean;
	};
	hideName?: boolean;
	hideImage?: boolean;
	hideStateBar?: boolean;
	deviceError?: DeviceErrorType;
}

export interface DeviceNameRendererParams {
	/**
	 * If true, will render device name as a readonly text input.
	 * @default false
	 */
	isDeviceNameReadonly?: boolean;
	/**
	 * If true, will render device name as simple text instead of a text input.
	 * @default false
	 */
	renderDeviceNameAsText?: boolean;
	/**
	 * If true, will hide the device online/offline state bar that is rendered left of the name.
	 * @default false
	 */
	shouldHideStateBar?: boolean;
	/**
	 * If true, will hide the identify button along with the identify placeholder.
	 * @default false
	 */
	shouldHideIdentify?: boolean;
}

/*
 * Interface to be implemented on the component parent if the device name is not readonly
 */
export interface EditableDeviceNameRendererParent {
	updateDeviceName(deviceId: string, deviceName: string): void;
	displayErrorMessage(deviceId: string, height: number): void;
}

/**
 * Interface to be implemented on the component parent if the device supports identify
 */
export interface IdentifiableDeviceNameRendererParent {
	setIdentify(deviceId: string, isIdentifying: boolean): void;
	getDeviceIdentifyingStatus$(deviceId: string): Observable<boolean>;
	isIdentifyDisabled$?(deviceId: string): Observable<boolean>;
}

/**
 * Convenience interface provided for devices that have editable names and are identifiable
 */
export type DeviceNameRendererParent = EditableDeviceNameRendererParent & IdentifiableDeviceNameRendererParent;

interface DeviceNameRendererComponentContext {
	componentParent: Partial<DeviceNameRendererParent>;
}

export type DeviceNameRendererComponentParams = ICellRendererParams<
	object,
	DeviceNameCellData,
	DeviceNameRendererComponentContext
> &
	DeviceNameRendererParams;

@Component({
	selector: 'sh-device-name-renderer',
	templateUrl: './device-name-renderer.component.html',
	styleUrl: './device-name-renderer.component.scss',
	imports: [
		CommonModule,
		AgGridModule,
		IdButtonComponent,
		DeviceStateBarComponent,
		DeviceImageComponent,
		InlineDeviceNameInputComponent,
		TranslocoModule,
		MatIconModule,
		MatProgressSpinnerModule
	]
})
@shUseTranslation('shAgGrid', /* istanbul ignore next */ (lang) => import(`../../../i18n/${lang}`))
export class DeviceNameRendererComponent implements ICellRendererAngularComp {
	protected readonly data = signal<DeviceNameCellData | null | undefined>(undefined);
	public readonly isOffline = computed(() => this.data()?.status === DeviceStatus.Offline);
	public readonly isDiscovered = computed(() => this.data()?.status === DeviceStatus.Discovered);
	public readonly hasError = computed(
		() =>
			this.data()?.status === DeviceStatus.Error ||
			this.data()?.deviceError === DeviceErrorType.IncompatibleFirmware
	);
	public readonly isNew = computed(() =>
		this.data()?.authenticatedState
			? this.data()?.authenticatedState === DeviceAuthenticationState.Uninitialized
			: false
	);
	public isIdentifying$!: Observable<boolean>;
	public isIdentifyDisabled$!: Observable<boolean>;

	public params!: DeviceNameRendererComponentParams;
	public deviceStatus = DeviceStatus;

	public agInit(params: DeviceNameRendererComponentParams): void {
		this.update(params);
		this.isIdentifying$ =
			params.context?.componentParent.getDeviceIdentifyingStatus$?.(this.data()?.id ?? '') ?? of(false);
		this.isIdentifyDisabled$ =
			params.context?.componentParent.isIdentifyDisabled$?.(this.data()?.id ?? '') ?? of(false);
	}

	public refresh(params: DeviceNameRendererComponentParams): boolean {
		this.update(params);
		return true;
	}

	public updateDeviceName(deviceId: string, deviceName: string): void {
		this.params.context.componentParent.updateDeviceName?.(deviceId, deviceName);
	}

	public setIdentify(deviceId: string, isIdentifying: boolean): void {
		this.params.context.componentParent.setIdentify?.(deviceId, !isIdentifying);
	}

	public displayErrorMessage(deviceId: string, height: number): void {
		this.params.context.componentParent.displayErrorMessage?.(deviceId, height);
	}

	private update(params: DeviceNameRendererComponentParams): void {
		this.params = params;
		this.data.set(params.value);
	}
}
